import { StageName } from "./offeringCatalog";

/**
 * Presentation tokens for the Products & Services stage matrix.
 *
 * The stage ramp (gray Build → teal Grow → navy Lead → red Scale) builds to
 * the brand navy and red, so it reads as a progression rather than a traffic
 * light. Dark values are lifted for contrast on slate surfaces. All class
 * strings are literal so Tailwind's JIT can compile the arbitrary values; do
 * not build these dynamically.
 */
export interface StageStyle {
  /** Solid accent: the header arrow bar. */
  accentBg: string;
  /** Arrowhead tip (a CSS border triangle) in the accent color. */
  arrowTip: string;
  /** Top rule above the stage header. */
  topBorder: string;
  /** Header background. */
  headBg: string;
  /** Included-cell background. */
  tintBg: string;
  /** Stage label and check-mark color. */
  ink: string;
  /** "You Are Here" pill. */
  pill: string;
}

export const stageStyles: Record<StageName, StageStyle> = {
  Build: {
    accentBg: "bg-[#707070] dark:bg-[#AEB9CC]",
    arrowTip: "border-l-[#707070] dark:border-l-[#AEB9CC]",
    topBorder: "border-t-[#707070] dark:border-t-[#AEB9CC]",
    headBg: "bg-[#F7F7F7] dark:bg-[#141C28]",
    tintBg: "bg-[#F1F1F1] dark:bg-[#18212E]",
    ink: "text-[#404040] dark:text-[#AEB9CC]",
    pill: "bg-[#707070] text-white dark:bg-[#AEB9CC] dark:text-slate-950",
  },
  Grow: {
    accentBg: "bg-[#2E7D80] dark:bg-[#3FB6BC]",
    arrowTip: "border-l-[#2E7D80] dark:border-l-[#3FB6BC]",
    topBorder: "border-t-[#2E7D80] dark:border-t-[#3FB6BC]",
    headBg: "bg-[#F4F8F8] dark:bg-[#0D2124]",
    tintBg: "bg-[#EDF2F2] dark:bg-[#0F272A]",
    ink: "text-[#1F5F62] dark:text-[#3FB6BC]",
    pill: "bg-[#2E7D80] text-white dark:bg-[#3FB6BC] dark:text-slate-950",
  },
  Lead: {
    accentBg: "bg-[#17064A] dark:bg-[#7B61FF]",
    arrowTip: "border-l-[#17064A] dark:border-l-[#7B61FF]",
    topBorder: "border-t-[#17064A] dark:border-t-[#7B61FF]",
    headBg: "bg-[#F5F4F9] dark:bg-[#16113A]",
    tintBg: "bg-[#EFEEF5] dark:bg-[#1A1547]",
    ink: "text-[#17064A] dark:text-[#8F7BFF]",
    pill: "bg-[#17064A] text-white dark:bg-[#7B61FF] dark:text-white",
  },
  Scale: {
    accentBg: "bg-[#B20030] dark:bg-[#FF3355]",
    arrowTip: "border-l-[#B20030] dark:border-l-[#FF3355]",
    topBorder: "border-t-[#B20030] dark:border-t-[#FF3355]",
    headBg: "bg-[#FCF6F7] dark:bg-[#2A0D17]",
    tintBg: "bg-[#F9EDF0] dark:bg-[#33101C]",
    ink: "text-[#B20030] dark:text-[#FF3355]",
    pill: "bg-[#B20030] text-white dark:bg-[#FF3355] dark:text-white",
  },
};

/** Paid add-on cell and legend swatch. */
export const ADD_ON_CELL =
  "bg-[#E3F2E8] text-[#1F7A4D] dark:bg-[#10261B] dark:text-[#4ADE80]";

/** Not-available cell and legend swatch. */
export const NOT_AVAILABLE_CELL =
  "bg-gray-50 text-gray-400 dark:bg-slate-900/60 dark:text-slate-600";

/**
 * The matrix header row: sticky positioning plus the rule separating it from
 * the rows scrolling underneath.
 *
 * `top-12` clears MainPageWrapper's own sticky page header (48px, z-[5]); the
 * z-index sits below both that and its stickyContent slot (z-[4]) so the page
 * chrome always wins. Sticky only takes effect above the matrix wrapper's
 * `min-[1340px]` breakpoint, where the wrapper stops being a scroll container;
 * the bottom rule applies at every width. Below `md` the header is not sticky:
 * it is tall once the summary stacks, and the cells label their own stage.
 */
export const MATRIX_HEADER =
  "md:sticky md:top-12 md:z-[3] border-b-2 border-gray-900 dark:border-slate-300";

/**
 * Shared by header and rows. From `md` up: name column + four 132px stage
 * columns, as in the design. Below `md` the name spans the full width and the
 * four stage cells sit underneath it — the fixed 528px stage block cannot fit
 * a phone, and every cell carries its own stage label there.
 */
export const GRID_COLUMNS =
  "grid-cols-4 md:grid-cols-[minmax(260px,1fr)_repeat(4,132px)]";

/** The name / summary cell: full row on phones, first column from `md`. */
export const NAME_CELL_SPAN = "col-span-4 md:col-span-1";
